/**
 * What is associated with THIS referrer converting.
 *
 * Each factor is stated as this client against the population on the same
 * measure, with **both numbers visible**. There is deliberately no combined
 * "influence score": a single number would be unfalsifiable, whereas
 * "+16.4 points against 35.4 overall" can be argued with, and being arguable
 * is the point of putting it on screen.
 *
 * Nothing here is a prediction, and the headline metric is an *index*, not a
 * percentage — see the note in the component.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Info, TrendingDown, TrendingUp } from "lucide-react";

import { api } from "@/api/client";
import { KpiCard } from "@/components/cards/KpiCard";
import type { AnalyticsEnvelope, Quality } from "@/types/api";
import { count, inr } from "@/utils/format";

interface Factor {
  factor: string; question: string;
  client_value: number | null; benchmark: number | null;
  gap: number | null; unit: string; direction: string;
  sample: number; note: string; reliable: boolean;
}
interface Coverage { name: string; activities: number }
interface Summary {
  found: boolean; client: string; referral_type: string; designation: string;
  area: string; information: number; admissions: number;
  admissions_per_100_information: number | null; revenue: number;
  population_admissions_per_100: number | null; strongest_factor: string | null;
}

function value(v: number | null, unit: string): string {
  if (v === null) return "—";
  if (unit === "inr") return inr(v);
  if (unit === "days") return `${v} d`;
  if (unit === "percent") return `${v}%`;
  return String(v);
}

export function ClientFactors({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["client-factors", clientId],
    queryFn: async () => {
      const { data } = await api.get<AnalyticsEnvelope<Summary>>(
        `/clients/${encodeURIComponent(clientId)}/factors`);
      return data;
    },
    staleTime: 600_000,
  });

  if (isLoading) return <div className="skeleton h-80" />;
  const s = data?.summary;
  if (!s?.found) {
    return <div className="card p-8 text-center text-sm text-ink-muted">
      No factors available for this client.
    </div>;
  }

  const factors = (data?.breakdown ?? []) as unknown as Factor[];
  const coverage = (data?.trend ?? []) as unknown as Coverage[];
  const widest = Math.max(1, ...factors.map((f) => Math.abs(f.gap ?? 0)));

  return (
    <div className="space-y-4">
      <div className="grid gap-3"
           style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))" }}>
        <KpiCard label="Admissions per 100 info"
                 value={s.admissions_per_100_information === null ? "—"
                   : String(s.admissions_per_100_information)}
                 footnote={`population ${s.population_admissions_per_100}`} />
        <KpiCard label="Information received" value={count(s.information)} />
        <KpiCard label="Admissions" value={count(s.admissions)} />
        <KpiCard label="Revenue" value={inr(s.revenue)} />
      </div>

      {/* The metric can exceed 100 by construction. Saying so once, plainly,
          beats a reader concluding the number is broken. */}
      <div className="card p-4 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0 text-ink-faint" />
        <p className="text-[12.5px] text-ink-soft">
          <strong>An index, not a percentage.</strong> Admissions are credited to
          a referrer through the lead record, while information items are
          activities logged against them — and only 56.5% of information items
          carry a client at all, against 99.7% of admissions. The two sides are
          not the same population, so this can exceed 100 and often does. It is
          useful as a comparison between referrers, not as a conversion rate.
        </p>
      </div>

      <section className="card p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h3 className="font-display font-semibold text-[15px]">
            This referrer against the population
          </h3>
          {s.strongest_factor && (
            <span className="text-[12px] text-ink-muted">
              strongest signal: <strong>{s.strongest_factor}</strong>
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead>
              <tr>
                {["Factor", "This referrer", "Population", "Gap", ""].map((h, i) => (
                  <th key={h || i} className={`label border-b border-rule px-2 py-2 ${
                    i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {factors.map((f) => (
                <tr key={f.factor} className="border-b border-rule last:border-0">
                  <td className="px-2 py-2">
                    <span className="block">{f.factor}</span>
                    <span className="block text-[11.5px] text-ink-faint">
                      {f.question}
                    </span>
                    {f.note && (
                      <span className="block text-[11px] text-ink-faint mt-0.5">
                        {f.note}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum">
                    {value(f.client_value, f.unit)}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tnum text-ink-muted">
                    {value(f.benchmark, f.unit)}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {f.gap === null ? (
                      <span className="text-ink-faint text-[11.5px]">no basis</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-mono tnum text-[12px]"
                            style={{ color: f.direction === "up" ? "#2F6B3C"
                              : f.direction === "down" ? "#9E3B2E" : "#5D6E6A" }}>
                        {f.direction === "up" ? <TrendingUp size={11} />
                          : f.direction === "down" ? <TrendingDown size={11} /> : null}
                        {f.gap > 0 ? "+" : ""}{f.gap}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 w-24">
                    {f.gap !== null && (
                      <span className="block h-1.5 rounded-full bg-surface-alt overflow-hidden">
                        <span className="block h-full rounded-full"
                              style={{
                                width: `${(Math.abs(f.gap) / widest) * 100}%`,
                                background: f.direction === "up" ? "#2F6B3C" : "#9E3B2E",
                                opacity: f.reliable ? 1 : 0.3,
                              }} />
                      </span>
                    )}
                    {!f.reliable && (
                      <span className="block text-[10.5px] text-warn mt-1">thin</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {coverage.length > 0 && (
        <section className="card p-4">
          <h3 className="font-display font-semibold text-[15px]">Who works this referrer</h3>
          <p className="text-[12px] text-ink-muted mb-2">
            By recorded activity. Coverage concentrated on one person is a
            continuity risk whatever the numbers say.
          </p>
          <ul className="space-y-1.5">
            {coverage.map((c) => (
              <li key={c.name} className="flex items-center gap-3 text-[13px]">
                <span className="w-52 shrink-0 truncate">{c.name}</span>
                <span className="flex-1 h-1.5 rounded-full bg-surface-alt overflow-hidden">
                  <span className="block h-full rounded-full bg-petrol"
                        style={{ width: `${(c.activities / coverage[0]!.activities) * 100}%` }} />
                </span>
                <span className="font-mono tnum text-[12.5px] w-14 text-right">
                  {count(c.activities)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(data?.quality as Quality)?.notes?.length ? (
        <div className="card p-4 space-y-1.5">
          <div className="label flex items-center gap-1.5">
            <AlertTriangle size={12} /> how to read this
          </div>
          {(data!.quality as Quality).notes.map((note) => (
            <p key={note} className="text-[12.5px] text-ink-soft">{note}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
